'use client';
import { useRef, useState } from 'react';
import { TabsBlock as ITabsBlock } from '../types';
import clsx from 'clsx';
import Image from 'next/image';
import { Swiper, SwiperSlide } from 'swiper/react';
import { A11y, Navigation, Pagination } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import 'swiper/css/pagination';
import NavigationArrow from '@/assets/nav_arrow.svg';
import ContentContainer from '../layout/ContentContainer';

const SwiperCarousel = ({
  content,
}: {
  content: ITabsBlock['tabs'][0]['content'];
}) => {
  const swiperRef = useRef<SwiperType | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  return content.images ? (
    <div>
      <div className="flex max-w-[80vw] justify-center rounded-2xl rounded-xl bg-white p-4 lg:max-w-[50vw]">
        <Swiper
          modules={[Navigation, Pagination, A11y]}
          slidesPerView={1}
          a11y={{ enabled: true }}
          onSwiper={swiper => {
            swiperRef.current = swiper;
          }}
          onSlideChange={swiper => setActiveIndex(swiper.activeIndex)}
          className="mySwiper w-full"
        >
          {content.images.map((image, idx) => (
            <SwiperSlide key={`media-block-swiper-slide-${idx}`}>
              <Image
                src={image.url || ''}
                alt={image.alt || ''}
                width={1200}
                height={400}
                className="w-full rounded-xl object-contain"
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* Custom pagination and arrows below, only if more than one image */}
      {content.images.length > 1 && (
        <div className="mt-6 flex flex-col items-center gap-4 lg:flex-row lg:justify-center">
          <div className="hidden gap-4 lg:flex">
            {/* Pagination dots - hidden on mobile */}
            {content.images?.map((_, idx) => (
              <button
                key={`media-block-pagination-dot-${idx}`}
                className={clsx(
                  'h-3 w-3 rounded-full transition-all hover:cursor-pointer',
                  activeIndex === idx ? 'bg-qf-purple' : 'bg-[#D9D9D9]',
                )}
                onClick={() => swiperRef.current?.slideTo(idx)}
              />
            ))}
          </div>
          {/* Arrows - always visible */}
          <div className="flex gap-1 rounded-full border border-gray-200">
            <button
              className="bg-qf-purple/10 m-1 flex h-10 w-10 items-center justify-center rounded-full hover:cursor-pointer hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => swiperRef.current?.slidePrev()}
              disabled={activeIndex === 0}
            >
              <NavigationArrow className="text-indigo-800" />
            </button>
            <button
              className="bg-qf-purple/10 m-1 flex h-10 w-10 items-center justify-center rounded-full hover:cursor-pointer hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => swiperRef.current?.slideNext()}
              disabled={activeIndex === (content.images?.length || 0) - 1}
            >
              <NavigationArrow className="rotate-180 text-indigo-800" />
            </button>
          </div>
        </div>
      )}
    </div>
  ) : null;
};

export default function TabsBlock({ block }: { block: ITabsBlock }) {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <ContentContainer>
      {block.title && (
        <div className="font-source-serif-pro py-4 text-center text-[24px] leading-[125%] font-semibold sm:py-8 sm:text-[32px]">
          {block.title}
        </div>
      )}

      <div>
        {/* Tab Navigation */}
        <div className="flex justify-center">
          <div className="md:bg-warmgrey-900 flex w-fit flex-wrap justify-center gap-2 rounded-full p-2 md:justify-start">
            {block.tabs.map((tab, index) => (
              <button
                key={index}
                onClick={() => setActiveTab(index)}
                className={`h-12 w-60 rounded-full px-11 py-2.5 text-sm font-medium transition-colors ${
                  activeTab === index
                    ? 'bg-qf-purple text-white'
                    : 'hover:border-qf-purple border border-gray-300 bg-white text-gray-900'
                }`}
              >
                {tab.text}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="min-h-[200px]">
          {/* Render all tabs to maintain swiper state */}
          {block.tabs.map((tab, index) => {
            const content = tab.content;
            const contentData = content.contents?.[0];
            const imagePosition = content.image_position ?? 'right';

            return contentData ? (
              <div
                key={index}
                className={clsx(
                  'flex w-full flex-col-reverse items-center justify-center gap-6 py-8 sm:gap-10 2xl:gap-24',
                  activeTab === index ? 'block' : 'hidden',
                  imagePosition === 'left'
                    ? 'lg:flex-row-reverse'
                    : 'lg:flex-row',
                )}
              >
                <div className="flex flex-col justify-center lg:w-[40%]">
                  {contentData.pre_title && (
                    <div
                      className="font-source-sans-pro mb-4 text-2xl font-bold text-indigo-800"
                      dangerouslySetInnerHTML={{
                        __html: contentData.pre_title,
                      }}
                    />
                  )}
                  {contentData.title && (
                    <div
                      className="font-source-sans-pro text-md text-qf-black mb-4 font-bold"
                      dangerouslySetInnerHTML={{ __html: contentData.title }}
                    />
                  )}
                  {contentData.body && (
                    <div
                      className="font-source-sans-pro text-md prose-content text-qf-black mb-6 max-w-2xl"
                      dangerouslySetInnerHTML={{
                        __html: contentData.body,
                      }}
                    />
                  )}
                </div>
                <div className="lg:w-[60%]">
                  <SwiperCarousel content={content} />
                </div>
              </div>
            ) : null;
          })}
        </div>
      </div>
    </ContentContainer>
  );
}
